import React, { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DependentsGrid } from './Dependents.styles';
import { FormValues } from '../../PersonalInfo.meta';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import {
  BENEFICIARIES,
  DEPENDENT_TYPE_OPTIONS,
  DependentToDelete,
  DependentToUpdate,
  DESCENDANTS,
  FormDependent,
  NEW_DEPENDENT,
} from './Dependents.meta';
import { InputMask } from '../../../../CommonComponents/OPL/Input/InputMask';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { DependentType } from '../../api/usePersonalInfoQuery';
import { UpdateDependentModal } from './components/UpdateDependentModal/UpdateDependentModal';
import { DeleteDependentModal } from './components/DeleteDependentModal/DeleteDependentModal';
import { SectionHeading } from '../../PersonalInfo.styles';

export const Dependents = () => {
  const [dependentToUpdate, setDependentToUpdate] = useState<DependentToUpdate | null>(null);

  const [dependentToDelete, setDependentToDelete] = useState<DependentToDelete | null>(null);

  const {
    control,
    register,
    setValue,
    watch,
    setFocus,
    formState: { errors },
  } = useFormContext<FormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'personalInfo.dependents',
    keyName: 'key',
  });

  const watchFieldArray = watch('personalInfo.dependents');
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const onDependentTypeChange = (
    newDependentType: DependentType,
    dependent: FormDependent,
    index: number,
  ) => {
    // We are changing a descendant to be a non-descendant
    if (
      dependent.id &&
      !DESCENDANTS.includes(newDependentType) &&
      DESCENDANTS.includes(dependent.type)
    ) {
      setDependentToUpdate({ index, type: dependent.type, newType: newDependentType });
    } else {
      setValue(`personalInfo.dependents.${index}.type`, newDependentType, { shouldDirty: true });

      if (newDependentType === DependentType.Grandchild) {
        setValue(`personalInfo.dependents.${index}.isDependent`, false, { shouldDirty: true });
      }

      if (newDependentType === DependentType.OtherDependent) {
        setValue(`personalInfo.dependents.${index}.isDependent`, true, { shouldDirty: true });
      }
    }
  };

  return (
    <>
      <section>
        <SectionHeading as="h2">Children & Other Dependents, and Beneficiaries</SectionHeading>

        <Box display="flex" flexDirection="column" gridGap={24}>
          {controlledFields.map((dependent, index) => {
            const isBeneficiary = BENEFICIARIES.includes(dependent.type);

            return (
              <DependentsGrid key={dependent.key} isBeneficiary={isBeneficiary}>
                <input {...register(`personalInfo.dependents.${index}.id`)} type="hidden" />

                <Input
                  {...register(`personalInfo.dependents.${index}.firstName`)}
                  label="First Name"
                  placeholder="First Name"
                  formVariant={
                    errors?.personalInfo?.dependents?.[index]?.firstName ? 'error' : undefined
                  }
                  helperText={errors?.personalInfo?.dependents?.[index]?.firstName?.message}
                />

                <Controller
                  control={control}
                  name={`personalInfo.dependents.${index}.type`}
                  render={({ field }) => (
                    <Select
                      innerRef={field.ref}
                      label="Type"
                      value={DEPENDENT_TYPE_OPTIONS.find(option => option.value === field.value)}
                      onChange={opt => {
                        if (opt) {
                          onDependentTypeChange(opt.value, dependent, index);
                        }
                      }}
                      options={DEPENDENT_TYPE_OPTIONS}
                    />
                  )}
                />

                {!isBeneficiary && (
                  <>
                    <InputMask
                      {...register(`personalInfo.dependents.${index}.dob`)}
                      label="Date of Birth"
                      mask="99/99/9999"
                      placeholder="MM/DD/YYYY"
                      leftIcon="far fa-calendar-alt"
                      formVariant={
                        errors?.personalInfo?.dependents?.[index]?.dob ? 'error' : undefined
                      }
                      helperText={errors?.personalInfo?.dependents?.[index]?.dob?.message}
                    />

                    <div>
                      <Label htmlFor={`profile--personal-info--dependent-${index}`} mb="4">
                        Dependent
                      </Label>

                      <Toggle
                        id={`profile--personal-info--dependent-${index}`}
                        {...register(`personalInfo.dependents.${index}.isDependent`)}
                        size="small"
                        mt="8"
                      >
                        {' '}
                      </Toggle>
                    </div>
                  </>
                )}

                <IconButton
                  icon="far fa-minus-circle"
                  variant="subtleInactive"
                  onClick={() => {
                    if (dependent.id) {
                      setDependentToDelete({ index });
                    } else {
                      remove(index);
                    }
                  }}
                  aria-label="Remove Dependent"
                  mt="24"
                />
              </DependentsGrid>
            );
          })}
        </Box>

        <Button
          leftIcon="fas fa-plus"
          variant="link"
          onClick={() => append(NEW_DEPENDENT)}
          mt={controlledFields.length > 0 ? '32' : '0'}
        >
          Add Children, Dependents, or Beneficiaries
        </Button>
      </section>

      {dependentToUpdate && (
        <UpdateDependentModal
          onUpdateDependent={() => {
            setValue(
              `personalInfo.dependents.${dependentToUpdate.index}.type`,
              dependentToUpdate.newType,
              {
                shouldDirty: true,
              },
            );

            if (dependentToUpdate.type === DependentType.OtherDependent) {
              setValue(`personalInfo.dependents.${dependentToUpdate.index}.isDependent`, true, {
                shouldDirty: true,
              });
            }
          }}
          onClose={() => {
            setDependentToUpdate(null);
            setFocus(`personalInfo.dependents.${dependentToUpdate.index}.type`);
          }}
        />
      )}

      {dependentToDelete && (
        <DeleteDependentModal
          onDeleteDependent={() => remove(dependentToDelete.index)}
          onClose={() => setDependentToDelete(null)}
        />
      )}
    </>
  );
};
